import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate, formatEUR } from "@/lib/constants";
import { useTableSort } from "@/lib/useTableSort";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, ArrowRight, Warning, CheckCircle, Receipt, FileText, Trash, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABELS = {
  draft: { label: "Entwurf", className: "bg-slate-100 text-slate-700" },
  ready: { label: "Bereit zur Freigabe", className: "bg-amber-100 text-amber-800" },
  issued: { label: "Freigegeben", className: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Storniert", className: "bg-rose-100 text-rose-800" },
};

export default function Nebenkosten() {
  const [billings, setBillings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [{ data: bs }, { data: ps }, { data: sc }] = await Promise.all([
      api.get("/nk-billings"),
      api.get("/properties"),
      api.get("/billing-schemas"),
    ]);
    setBillings(bs);
    setProperties(ps);
    setSchemas(sc);
  };
  useEffect(() => { load(); }, []);

  const propertyMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);
  const enriched = billings.map((b) => ({ ...b, property_name: propertyMap[b.property_id]?.name || "—" }));
  const { sorted, SortableTh } = useTableSort(enriched, "period_from", "desc", {
    title: (b) => b.title || "",
    property: (b) => b.property_name || "",
    period_from: (b) => b.period_from || "",
    status: (b) => b.status || "",
    positions: (b) => (b.positions || []).length,
  });

  return (
    <div className="space-y-4" data-testid="nebenkosten-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Nebenkostenabrechnung</h1>
          <p className="text-sm text-slate-500 mt-1">Betriebskosten-Abrechnungen je Objekt – Schema-basiert mit Anlagen-Pflicht</p>
        </div>
        <div className="flex gap-2">
          <Link to="/einstellungen?tab=nk-schemata">
            <Button variant="outline" data-testid="manage-schemas-btn" className="gap-2">
              <FileText size={16} /> Schemata verwalten
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="new-billing-btn">
                <Plus size={16} /> Neue Abrechnung
              </Button>
            </DialogTrigger>
            <NewBillingDialog properties={properties} schemas={schemas} onSaved={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-700 text-left">
            <tr>
              <SortableTh field="title">Titel</SortableTh>
              <SortableTh field="property">Objekt</SortableTh>
              <SortableTh field="period_from">Zeitraum</SortableTh>
              <SortableTh field="positions">Positionen</SortableTh>
              <SortableTh field="status">Status</SortableTh>
              <Th className="w-16 text-right"></Th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">
                <Receipt size={32} className="mx-auto mb-2 text-slate-300" />
                Noch keine Abrechnungen angelegt.
              </td></tr>
            )}
            {sorted.map((b) => {
              const s = STATUS_LABELS[b.status] || STATUS_LABELS.draft;
              const totalPositions = (b.positions || []).length;
              const missingAnlagen = (b.positions || []).filter((p) => !p.attachment_doc_id).length;
              return (
                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`billing-row-${b.id}`}>
                  <Td className="font-medium text-slate-900">{b.title}</Td>
                  <Td>{b.property_name}</Td>
                  <Td className="tabular-nums">{formatDate(b.period_from)} – {formatDate(b.period_to)}</Td>
                  <Td>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="tabular-nums">{totalPositions}</span>
                      {missingAnlagen > 0 && (
                        <span className="text-amber-700 inline-flex items-center gap-1" title={`${missingAnlagen} ohne Anlage`}>
                          <Warning size={12} weight="fill" />
                          {missingAnlagen} ohne Anlage
                        </span>
                      )}
                      {totalPositions > 0 && missingAnlagen === 0 && (
                        <span className="text-emerald-700 inline-flex items-center gap-1">
                          <CheckCircle size={12} weight="fill" /> komplett
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span></Td>
                  <Td className="text-right">
                    <Link to={`/nebenkosten/${b.id}`} data-testid={`billing-open-${b.id}`}>
                      <Button size="icon" variant="ghost" className="h-7 w-7"><ArrowRight size={14} /></Button>
                    </Link>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewBillingDialog({ properties, schemas, onSaved }) {
  const [form, setForm] = useState({
    property_id: "",
    schema_id: "",
    period_from: new Date(new Date().getFullYear() - 1, 0, 1).toISOString().slice(0, 10),
    period_to: new Date(new Date().getFullYear() - 1, 11, 31).toISOString().slice(0, 10),
    title: "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id || !form.schema_id) { toast.error("Objekt und Schema erforderlich"); return; }
    try {
      await api.post("/nk-billings", { ...form });
      toast.success("Abrechnung angelegt");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Neue Betriebskostenabrechnung</DialogTitle>
        <DialogDescription>Wählen Sie Objekt, Abrechnungszeitraum und Schema. Positionen werden im Anschluss hinzugefügt.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <F label="Objekt">
          <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
            <SelectTrigger data-testid="billing-property"><SelectValue placeholder="Objekt auswählen" /></SelectTrigger>
            <SelectContent>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.property_number} · {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        <F label="Schema">
          <Select value={form.schema_id} onValueChange={(v) => set("schema_id", v)}>
            <SelectTrigger data-testid="billing-schema"><SelectValue placeholder="Schema auswählen" /></SelectTrigger>
            <SelectContent>
              {schemas.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}{s.is_system_seed ? " · Standard" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Zeitraum von">
            <Input type="date" required value={form.period_from} onChange={(e) => set("period_from", e.target.value)} data-testid="billing-from" />
          </F>
          <F label="Zeitraum bis">
            <Input type="date" required value={form.period_to} onChange={(e) => set("period_to", e.target.value)} data-testid="billing-to" />
          </F>
        </div>
        <F label="Titel (optional)">
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="z. B. Betriebskostenabrechnung 2025" />
        </F>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="billing-create-btn">Anlegen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}
